// Account deletion (src/aithora/account-deletion-routes.ts): Google Play and Apple require
// it in the app, on a public web page and by email. Used by the mobile Me screen and the
// console's /delete-account page.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export type AccountKind = 'owner' | 'team' | 'facility_staff' | 'resident' | 'family' | 'home_member' | 'unknown';
/** immediate = deleted now; request_facility / request_owner = a request is filed; refused. */
export type DeletionMode = 'immediate' | 'request_facility' | 'request_owner' | 'refused';

export interface AccountDeletionPlan {
  real: true;
  kind: AccountKind;
  mode: DeletionMode;
  /** Why a request is filed or the deletion refused, or null. */
  reason: string | null;
  /** What is deleted, in plain English. */
  deletes: string[];
  /** What is kept, and why, in plain English. */
  keeps: string[];
  supportEmail: string;
  pendingRequest: { id: string; status: string; createdAt: string } | null;
  /** The console's Delete my account dialog: its title, lead, groups and the last-Admin rule. */
  dialog?: AccountDeletionDialog;
  /** Active Admins at a Facility Admin's building (the last-Admin check); null for other roles. */
  adminCount?: number | null;
  /** The public page for anyone without the app. */
  publicPage?: string;
}

export interface AccountDeletionDialog {
  title: string;
  lead: string;
  groups: { head: string; tone: 'deleted' | 'kept'; items: { t: string; why: string | null }[] }[];
  rule: string | null;
}

export interface AccountDeleteResult {
  real: true;
  deleted: boolean;
  requested: boolean;
  message: string;
  kind?: AccountKind;
  requestId?: string;
  status?: string;
}

/** GET /account/deletion: what deleting my account does, and how. */
export const accountDeletionPlan = route<AccountDeletionPlan>()('GET', `${A}/account/deletion`, { honest: true });

/** POST /account/delete { confirm: 'DELETE' }: delete now, or file the request. */
export const accountDelete = route<AccountDeleteResult, void, { confirm: 'DELETE'; details?: string }>()('POST', `${A}/account/delete`, { honest: true });

/** POST /delete-account/request: public, signed out. Same answer whether or not the email has an account. */
export const deleteAccountRequest = route<{ ok: boolean; message: string }, void, { email: string; name?: string; details?: string }>()(
  'POST', `${A}/delete-account/request`, { anonymous: true });

// ── The admin tool for emailed and web requests (Settings, Users & Permissions) ──

export interface DeletionRequestMatch {
  userId: string;
  role: string;
  kind: AccountKind;
  organizationId: string | null;
  organizationName: string | null;
  /** The account holder's name. */
  name?: string | null;
}

export interface DeletionRequest {
  id: string;
  channel: 'app' | 'web' | 'email';
  status: 'received' | 'forwarded_to_facility' | 'completed' | 'declined' | 'cancelled';
  email: string | null;
  name: string | null;
  details: string | null;
  userId: string | null;
  organizationId: string | null;
  accountKind: AccountKind | null;
  residentName: string | null;
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  /** Accounts with the requested email (open requests only). */
  matches?: DeletionRequestMatch[];
}

export interface DeletionRequestsResponse { real: true; requests: DeletionRequest[] }

/** GET /admin/account-deletion/requests?status=: a signed-in Super Admin or Operator. */
export const adminDeletionRequests = route<DeletionRequestsResponse, { status?: string }>()('GET', `${A}/admin/account-deletion/requests`, { honest: true });

/** POST /admin/account-deletion/requests: log a request that came by email. */
export const adminDeletionRequestLog = route<{ real: true; request: DeletionRequest }, void, { email: string; name?: string; details?: string }>()(
  'POST', `${A}/admin/account-deletion/requests`, { honest: true });

/** POST .../:id/execute: carry out a verified request (a resident's is forwarded to the facility). */
export const adminDeletionRequestExecute = route<
  { real: true; deleted: boolean; forwarded?: boolean; kind?: AccountKind; message?: string; subscription?: string | null },
  void,
  { userId: string; verification: string; confirm: 'DELETE' }
>()('POST', `${A}/admin/account-deletion/requests/:id/execute`, { honest: true });

/** POST .../:id/decline { reason }. */
export const adminDeletionRequestDecline = route<{ real: true; request: DeletionRequest }, void, { reason: string }>()(
  'POST', `${A}/admin/account-deletion/requests/:id/decline`, { honest: true });
